import { useEffect, useState } from 'react';

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';

import { useCreatePricingRule, useUpdatePricingRule } from '../../hooks';
import { RULE_TYPE_OPTIONS } from '../../model/options';
import type {
  PricingRuleRequest,
  PricingRuleResponse,
  PricingRuleUpdateRequest,
  RuleType,
} from '../../model/types';
import type { ToastSeverity } from '../../lib/useToast';

interface RuleFormDialogProps {
  open: boolean;
  /** The rule being edited, or `null` to create. */
  rule: PricingRuleResponse | null;
  onClose: () => void;
  onResult: (severity: ToastSeverity, message: string) => void;
}

function num(value: string): number | undefined {
  const t = value.trim();
  if (t === '') return undefined;
  const n = Number(t);
  return Number.isNaN(n) ? undefined : n;
}

function toIso(local: string): string | undefined {
  if (!local) return undefined;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

function toLocal(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // YYYY-MM-DDTHH:mm in local time for <input type="datetime-local">
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}

/** Create / update a pricing rule. On edit, productId + ruleType are immutable. */
export function RuleFormDialog({ open, rule, onClose, onResult }: RuleFormDialogProps) {
  const create = useCreatePricingRule();
  const update = useUpdatePricingRule();
  const isEdit = rule !== null;
  const pending = create.isPending || update.isPending;

  const [productId, setProductId] = useState('');
  const [ruleType, setRuleType] = useState<RuleType>('PRODUCT_DISCOUNT');
  const [discountPercent, setDiscountPercent] = useState('');
  const [priceOverride, setPriceOverride] = useState('');
  const [taxRate, setTaxRate] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [priority, setPriority] = useState('0');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');

  useEffect(() => {
    if (!open) return;
    setProductId(rule?.productId ?? '');
    setRuleType((rule?.ruleType as RuleType) ?? 'PRODUCT_DISCOUNT');
    setDiscountPercent(rule?.discountPercent != null ? String(rule.discountPercent) : '');
    setPriceOverride(rule?.priceOverride != null ? String(rule.priceOverride) : '');
    setTaxRate(rule?.taxRate != null ? String(rule.taxRate) : '');
    setPromoCode(rule?.promoCode ?? '');
    setPriority(rule?.priority != null ? String(rule.priority) : '0');
    setStartsAt(toLocal(rule?.startsAt));
    setEndsAt(toLocal(rule?.endsAt));
  }, [open, rule]);

  const valid = isEdit || (productId.trim() !== '' && ruleType !== undefined);

  const submit = () => {
    if (isEdit && rule) {
      const body: PricingRuleUpdateRequest = {
        discountPercent: num(discountPercent),
        priceOverride: num(priceOverride),
        taxRate: num(taxRate),
        startsAt: toIso(startsAt),
        endsAt: toIso(endsAt),
      };
      update.mutate(
        { ruleId: rule.id, body },
        {
          onSuccess: () => {
            onResult('success', 'Правило обновлено.');
            onClose();
          },
          onError: (error) => onResult('error', error.message),
        },
      );
      return;
    }
    const body: PricingRuleRequest = {
      productId: productId.trim(),
      ruleType,
      discountPercent: num(discountPercent),
      priceOverride: num(priceOverride),
      taxRate: num(taxRate),
      promoCode: promoCode.trim() || undefined,
      priority: num(priority) ?? 0,
      startsAt: toIso(startsAt),
      endsAt: toIso(endsAt),
    };
    create.mutate(body, {
      onSuccess: () => {
        onResult('success', 'Правило создано.');
        onClose();
      },
      onError: (error) => onResult('error', error.message),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? 'Редактировать правило цены' : 'Новое правило цены'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            label="ID товара"
            required
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            error={!isEdit && productId.trim() === ''}
            disabled={isEdit}
            fullWidth
            slotProps={{ htmlInput: { style: { fontFamily: 'JetBrains Mono, monospace' } } }}
          />
          <TextField
            select
            label="Тип правила"
            value={ruleType}
            onChange={(e) => setRuleType(e.target.value as RuleType)}
            disabled={isEdit}
            fullWidth
          >
            {RULE_TYPE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}>
            <TextField
              label="Скидка, %"
              type="number"
              value={discountPercent}
              onChange={(e) => setDiscountPercent(e.target.value)}
              fullWidth
              slotProps={{ htmlInput: { min: 0, max: 100, step: '0.1' } }}
            />
            <TextField
              label="Фикс. цена, ₽"
              type="number"
              value={priceOverride}
              onChange={(e) => setPriceOverride(e.target.value)}
              fullWidth
              slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}>
            <TextField
              label="Ставка налога (0–1)"
              type="number"
              value={taxRate}
              onChange={(e) => setTaxRate(e.target.value)}
              fullWidth
              slotProps={{ htmlInput: { min: 0, max: 1, step: '0.01' } }}
            />
            <TextField
              label="Приоритет"
              type="number"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              disabled={isEdit}
              fullWidth
              slotProps={{ htmlInput: { step: 1 } }}
            />
          </Stack>
          {!isEdit && ruleType === 'PROMO_CODE' ? (
            <TextField
              label="Промокод"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              fullWidth
            />
          ) : null}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}>
            <TextField
              label="Действует с"
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="Действует по"
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>
          Отмена
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!valid || pending}
          startIcon={pending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
}
